import { getSelectedCountryCode } from "@utils/countryUtils";
import { getValidAccessToken, check401 } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

// API Response Types
export interface FacilityDocument {
  id: number;
  document_code: string;
  document_name: string;
  source_url: string;
  compliance_type: string;
  notes: string;
  as_of_date: string;
}

export interface FacilityType {
  id: number;
  type_slug: string;
  display_name: string;
  category: string;
  documents: FacilityDocument[];
  facility_description: string;
}

export interface FacilityTypesResponse {
  facility_types: FacilityType[];
}

// Transformed Types for UI
export interface TransformedCategory {
  id: string;
  title: string;
  description: string;
  facilityTypes: FacilityType[];
}

export interface TransformedSection {
  id: string;
  title: string;
  description: string;
  documents: FacilityDocument[];
  isExpanded?: boolean;
}

// Service configuration
const FACILITY_TYPES_API_BASE = getApiOrigin();
const FACILITY_TYPES_PATH = "/api/v1/facility-types";

/** Session cache so Mackay map-time prefetch makes click-time RHC id resolve sync (CI 33332). */
const _facilityTypesCache = new Map<string, FacilityTypesResponse>();
const _facilityTypesInflight = new Map<string, Promise<FacilityTypesResponse>>();

export function clearFacilityTypesCacheForTests(): void {
  _facilityTypesCache.clear();
  _facilityTypesInflight.clear();
}

// Main service functions
export async function getFacilityTypes(
  country: string = getSelectedCountryCode()
): Promise<FacilityTypesResponse> {
  const cacheKey = `WA|${country}`;
  const cached = _facilityTypesCache.get(cacheKey);
  if (cached) return cached;
  const inflight = _facilityTypesInflight.get(cacheKey);
  if (inflight) return inflight;

  const url = `${FACILITY_TYPES_API_BASE}${FACILITY_TYPES_PATH}?state_province=WA&country=${encodeURIComponent(
    country
  )}`;

  const request = (async () => {
    const token = getValidAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers,
    });

    check401(response);

    if (!response.ok) {
      const text = await response.text();
      throw new Error(
        text || `Failed to fetch facility types: ${response.status}`
      );
    }

    const data = (await response.json()) as FacilityTypesResponse;
    _facilityTypesCache.set(cacheKey, data);
    return data;
  })();

  _facilityTypesInflight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    _facilityTypesInflight.delete(cacheKey);
  }
}

// Transform API data to match existing component structure
export function transformFacilityTypesToCategories(
  facilityTypes: FacilityType[]
): TransformedCategory[] {
  // Group facility types by category
  const categoryMap = new Map<string, FacilityType[]>();

  facilityTypes.forEach((facilityType) => {
    const categoryKey = facilityType.category;
    if (!categoryMap.has(categoryKey)) {
      categoryMap.set(categoryKey, []);
    }
    categoryMap.get(categoryKey)!.push(facilityType);
  });

  // Convert to categories array
  const categories: TransformedCategory[] = Array.from(
    categoryMap.entries()
  ).map(([categoryName, types], index) => {
    // Generate a slug-like ID from category name
    const categoryId = categoryName
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    return {
      id: categoryId,
      title: categoryName,
      description: `Compliance solutions for ${categoryName.toLowerCase()} facilities`,
      facilityTypes: types,
    };
  });

  return categories;
}

export function transformFacilityTypesToSections(
  facilityTypes: FacilityType[]
): TransformedSection[] {
  return facilityTypes.map((facilityType) => ({
    id: facilityType.type_slug,
    title: facilityType.display_name,
    description: facilityType.facility_description,
    // facilityType.documents.length > 0
    //   ? `${facilityType.documents.length} compliance documents and regulations`
    //   : "Regulatory compliance requirements and standards",
    documents: facilityType.documents,
    isExpanded: false,
  }));
}

// Create category to sections mapping for the selected category
export function createCategoryToSectionsMap(
  categories: TransformedCategory[]
): Record<string, string[]> {
  const mapping: Record<string, string[]> = {};

  categories.forEach((category) => {
    mapping[category.id] = category.facilityTypes.map((ft) => ft.type_slug);
  });

  return mapping;
}

// Provider type options for USA (when NPI is ambiguous – no or multiple facility types)
export interface ProviderTypeOption {
  slug: string;
  display_name: string;
}

export interface ProviderTypeOptionsResponse {
  provider_type_options: ProviderTypeOption[];
  country_code: string;
}

export async function getProviderTypeOptions(
  countryCode: string = getSelectedCountryCode()
): Promise<ProviderTypeOptionsResponse> {
  const url = `${FACILITY_TYPES_API_BASE}${FACILITY_TYPES_PATH}/provider-type-options?country_code=${encodeURIComponent(
    countryCode
  )}`;

  const token = getValidAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, { method: "GET", headers });

  check401(response);

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      text || `Failed to fetch provider type options: ${response.status}`
    );
  }

  return (await response.json()) as ProviderTypeOptionsResponse;
}

/**
 * Certified provider type options (USA types with code and SOM appendix only).
 * Used when NPI has practitioner/group primary taxonomy and POS did not resolve —
 * user must select facility type; this list is for that dropdown.
 */
export async function getCertifiedProviderTypeOptions(
  countryCode: string = getSelectedCountryCode()
): Promise<ProviderTypeOptionsResponse> {
  const url = `${FACILITY_TYPES_API_BASE}${FACILITY_TYPES_PATH}/certified-provider-type-options?country_code=${encodeURIComponent(
    countryCode
  )}`;

  const token = getValidAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, { method: "GET", headers });

  check401(response);

  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      text || `Failed to fetch certified provider type options: ${response.status}`
    );
  }

  return (await response.json()) as ProviderTypeOptionsResponse;
}
